import React from 'react';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Lottie from 'react-lottie';

import { schema } from './schemaValidation';
import AuthActions from 'store/ducks/auth';
import animationData from 'assets/animation.json';
import GutoBannerLogo from './GutoBannerLogo';

import {
  LoginPage,
  BannerColumn,
  DecorCircleOuter,
  DecorCircleInner,
  Tagline,
  BannerSubtitle,
  FeaturesRow,
  FeatureItem,
  FeatureIconWrap,
  FeatureLabel,
  FormColumn,
  FormInner,
  FormHeaderBlock,
  FormTitle,
  FormSubtitle,
  UnderlineFieldWrap,
  UnderlineInput,
  FieldError,
  RememberRow,
  RememberLabel,
  ForgotLinkStyled,
  SubmitButton,
  GridLoading,
  WhiteCheckbox,
} from './styles';

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

function IconClock() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F5C300" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F5C300" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconTruck() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F5C300" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

export default function SignIn() {
  const dispatch = useDispatch();
  const authChecked = useSelector((state) => state.auth.authChecked);
  const isLoading = useSelector((state) => state.auth.isLoading);
  const signedIn = useSelector((state) => state.auth.signedIn);

  function handleSubmitted(values) {
    dispatch(AuthActions.signInRequest(values.email, values.password, values.rememberMe));
  }

  if (!authChecked)
    return (
      <GridLoading>
        <Lottie options={defaultOptions} height={400} width={400} />
      </GridLoading>
    );

  if (isLoading)
    return (
      <GridLoading>
        <Lottie options={defaultOptions} height={400} width={400} />
      </GridLoading>
    );

  return !signedIn ? (
    <LoginPage>
      <BannerColumn>
        <DecorCircleOuter />
        <DecorCircleInner />
        <GutoBannerLogo />
        <BannerSubtitle>
          Conectando origens a destinos com agilidade e tecnologia.
        </BannerSubtitle>
        <FeaturesRow>
          <FeatureItem>
            <FeatureIconWrap>
              <IconClock />
            </FeatureIconWrap>
            <FeatureLabel>AGILIDADE</FeatureLabel>
          </FeatureItem>
          <FeatureItem>
            <FeatureIconWrap>
              <IconPin />
            </FeatureIconWrap>
            <FeatureLabel>RASTREIO</FeatureLabel>
          </FeatureItem>
          <FeatureItem>
            <FeatureIconWrap>
              <IconTruck />
            </FeatureIconWrap>
            <FeatureLabel>ENTREGAS</FeatureLabel>
          </FeatureItem>
        </FeaturesRow>
      </BannerColumn>

      <FormColumn>
        <Formik
          enableReinitialize
          initialValues={{
            email: '',
            password: '',
            rememberMe: false,
          }}
          onSubmit={(values) => handleSubmitted(values)}
          validationSchema={schema}
        >
          {({ values, handleChange, setFieldValue, errors, touched, handleSubmit }) => (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <FormInner>
                <FormHeaderBlock>
                  <FormTitle>Bem-vindo</FormTitle>
                  <FormSubtitle>Faça login para acessar o sistema.</FormSubtitle>
                </FormHeaderBlock>

                <div>
                  <UnderlineFieldWrap>
                    <UnderlineInput
                      name="email"
                      placeholder="Login"
                      autoComplete="username"
                      value={values.email}
                      onChange={handleChange}
                    />
                  </UnderlineFieldWrap>
                  {touched.email && errors.email ? (
                    <FieldError>{errors.email}</FieldError>
                  ) : null}
                </div>

                <div>
                  <UnderlineFieldWrap>
                    <UnderlineInput
                      name="password"
                      type="password"
                      placeholder="Senha"
                      autoComplete="current-password"
                      value={values.password}
                      onChange={handleChange}
                    />
                  </UnderlineFieldWrap>
                  {touched.password && errors.password ? (
                    <FieldError>{errors.password}</FieldError>
                  ) : null}
                </div>

                <RememberRow>
                  <WhiteCheckbox
                    checked={values.rememberMe}
                    onChange={(e) => setFieldValue('rememberMe', e.target.checked)}
                    name="rememberMe"
                    inputProps={{ id: 'signin-remember' }}
                  />
                  <RememberLabel htmlFor="signin-remember">Permanecer conectado</RememberLabel>
                </RememberRow>

                <ForgotLinkStyled to="/forgot">Recuperar senha</ForgotLinkStyled>

                <SubmitButton type="submit">ENTRAR</SubmitButton>
              </FormInner>
            </form>
          )}
        </Formik>
      </FormColumn>
    </LoginPage>
  ) : (
    <Redirect to="/" />
  );
}
